import React, { useEffect, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaTrash } from "react-icons/fa";
import {
  DECIMAL_RE,
  defaultStoneFields,
  handleDecimalKeyDown,
  handleDecimalPaste,
  handleIntegerBeforeInput,
  handleIntegerKeyDown,
  handleIntegerPaste,
} from "../../config/fieldsHelper";
import { useSelector } from "react-redux";

const StonePopUp = ({ initialStones = [], setOpenStoneModal, onSave, itemIndex = 0 }) => {
  const stonesData = useSelector((state) => state.stones?.stones);

  const { register, control, handleSubmit, setValue, getValues, setFocus, watch, reset } = useForm({
    defaultValues: {
      stones: initialStones?.length ? initialStones : [defaultStoneFields],
    },
  });

  useEffect(() => {
    reset({ stones: initialStones?.length ? initialStones : [defaultStoneFields] });
  }, [initialStones, reset]);

  const { fields, append, remove } = useFieldArray({ control, name: "stones" });
  const rowRefs = useRef([]);

  // auto-focus first row on open
  useEffect(() => {
    setTimeout(() => {
      setFocus("stones.0.stnPopupName");
      rowRefs.current[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 250);
  }, [setFocus]);

  const scrollRowIntoView = (index) => {
    const el = rowRefs.current[index];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const recomputeAmt = (i) => {
    const wt = parseFloat(getValues(`stones.${i}.stnPopupWt`));
    const rWt = parseFloat(getValues(`stones.${i}.stnPopupRateByWt`));
    const pcs = parseInt(getValues(`stones.${i}.stnPopupPcs`), 10);
    const rPc = parseFloat(getValues(`stones.${i}.stnPopupRateByPcs`));
    let amt = "";
    if (!isNaN(wt) && !isNaN(rWt) && (isNaN(pcs) || pcs === 0)) amt = (wt * rWt).toFixed(2);
    else if (!isNaN(pcs) && !isNaN(rPc)) amt = (pcs * rPc).toFixed(2);
    setValue(`stones.${i}.stnPopupAmt`, amt, { shouldDirty: true, shouldValidate: true });
  };

  const handleSaveClick = (data) => {
    onSave?.(data?.stones);
    setOpenStoneModal(false);
  };

  const watchAll = watch("stones");
  const totals = {
    totalWt: watchAll.reduce((s, x) => s + parseFloat(x?.stnPopupWt || 0), 0).toFixed(3),
    totalPcs: watchAll.reduce((s, x) => s + parseInt(x?.stnPopupPcs || 0), 0),
    totalAmt: watchAll.reduce((s, x) => s + parseFloat(x?.stnPopupAmt || 0), 0).toFixed(2),
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-[#f7f4ff] border border-violet-300 rounded-lg shadow-xl w-[80vw] max-w-4xl max-h-[260px] flex flex-col relative"
        >
          {/* Header */}
          <div className="sticky top-0 bg-[#f7f4ff] z-20 border-b border-violet-200 p-2 flex justify-between items-center">
            <h5 className="text-sm font-semibold text-violet-700 flex items-center gap-2">
              💎 Stones — <span className="text-gray-600">Item #{itemIndex + 1}</span>
            </h5>
            <button
              type="button"
              onClick={() => setOpenStoneModal(false)}
              className="text-xs text-gray-500 hover:text-red-600 transition"
            >
              ✕ Close
            </button>
          </div>

          {/* Scrollable Rows */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {fields.map((field, idx) => {
              const unit = watch(`stones.${idx}.unit`);
              const hasWt = !!watch(`stones.${idx}.stnPopupWt`);
              const hasRateWt = !!watch(`stones.${idx}.stnPopupRateByWt`);
              const hasPcs = !!watch(`stones.${idx}.stnPopupPcs`);
              const hasRatePcs = !!watch(`stones.${idx}.stnPopupRateByPcs`);
              const weightMode = hasWt || hasRateWt;
              const pcsMode = hasPcs || hasRatePcs;

              return (
                <div
                  key={field.id}
                  ref={(el) => (rowRefs.current[idx] = el)}
                  className="border border-violet-200 bg-white rounded-md p-2 shadow-sm relative"
                >
                  {/* Sr No */}
                  <div className="absolute -left-2 -top-2 bg-violet-500 text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center shadow">
                    {idx + 1}
                  </div>

                  {/* === First Line === */}
                  <div className="grid grid-cols-6 gap-2 mb-2">
                    {/* Name */}
                    <div>
                      <label className="text-[10px] text-gray-600">Name</label>
                      <select
                        {...register(`stones.${idx}.stnPopupName`, {
                          onChange: (e) => {
                            const id = e.target.value;
                            const sel = stonesData?.find((s) => String(s?._id) === String(id));
                            if (!sel) return;

                            // 🔹 Auto-fill logic restored
                            setValue(`stones.${idx}.unit`, sel?.unit || null);
                            setValue(`stones.${idx}.shape`, sel?.shape ? sel.shape : "");
                            setValue(`stones.${idx}.size`, sel?.size ? sel.size : "");
                            setValue(`stones.${idx}.color`, sel?.color ? sel.color : "");
                            setValue(`stones.${idx}.settingType`, sel?.settingType ? sel.settingType : "");

                            setValue(`stones.${idx}.stnPopupWt`, sel?.stnPopupWt ?? "");
                            setValue(`stones.${idx}.stnPopupRateByWt`, sel?.stnPopupRateByWt ?? "");
                            setValue(`stones.${idx}.stnPopupPcs`, sel?.stnPopupPcs ?? "");
                            setValue(`stones.${idx}.stnPopupRateByPcs`, sel?.stnPopupRateByPcs ?? "");
                            setValue(`stones.${idx}.stnPopupDescription`, sel?.stnPopupDescription ?? "");
                            setValue(`stones.${idx}.stnPopupAmt`, sel?.stnPopupAmount ?? "");
                          },
                        })}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300 focus:ring-2 focus:ring-violet-300 outline-none"
                      >
                        <option value="">--Select--</option>
                        {stonesData?.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.stoneName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Wt */}
                    <div>
                      <label className="text-[10px] text-gray-600">
                        Wt {unit === "gram" ? "(gm)" : "(ct)"}
                      </label>
                      <input
                        {...register(`stones.${idx}.stnPopupWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Wt"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Wt */}
                    <div>
                      <label className="text-[10px] text-gray-600">Rate/Wt</label>
                      <input
                        {...register(`stones.${idx}.stnPopupRateByWt`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Rate/Wt"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupRateByWt`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={pcsMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Pcs</label>
                      <input
                        type="text"
                        {...register(`stones.${idx}.stnPopupPcs`, {
                          onChange: () => recomputeAmt(idx),
                          setValueAs: (v) => (v === "" ? "" : String(v).replace(/\D/g, "")),
                        })}
                        placeholder="Pcs"
                        onBeforeInput={handleIntegerBeforeInput}
                        onKeyDown={handleIntegerKeyDown}
                        onPaste={handleIntegerPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Rate/Pcs */}
                    <div>
                      <label className="text-[10px] text-gray-600">Rate/Pcs</label>
                      <input
                        {...register(`stones.${idx}.stnPopupRateByPcs`, {
                          onChange: () => recomputeAmt(idx),
                          validate: (v) => v === "" || DECIMAL_RE.test(String(v)) || "Only numbers",
                        })}
                        placeholder="Rate/Pcs"
                        inputMode="decimal"
                        onKeyDown={(e) =>
                          handleDecimalKeyDown(e, watch(`stones.${idx}.stnPopupRateByPcs`) ?? "")
                        }
                        onPaste={handleDecimalPaste}
                        disabled={weightMode}
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Amt */}
                    <div>
                      <label className="text-[10px] text-gray-600">Amt</label>
                      <input
                        {...register(`stones.${idx}.stnPopupAmt`)}
                        placeholder="0.00"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>
                  </div>

                  {/* Second Line */}
                  <div className="grid grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="text-[10px] text-gray-600">Deduct</label>
                      <select
                        {...register(`stones.${idx}.stnPopupDeduct`)}
                        className="border px-2 py-0.5 rounded text-xs w-full border-gray-300"
                      >
                        <option value="">--Select--</option>
                        <option value="deduct">Deduct</option>
                        <option value="add">Add</option>
                        <option value="nonDeduct">Non-Deduct</option>
                      </select>
                    </div>

                    <div className="col-span-2">
                      <label className="text-[10px] text-gray-600">Description</label>
                      <input
                        {...register(`stones.${idx}.stnPopupDescription`)}
                        placeholder="Description"
                        className="border px-2 py-0.5 rounded text-xs w-full"
                      />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2 justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          const prev = idx > 0 ? idx - 1 : 0;
                          remove(idx);
                          setTimeout(() => {
                            setFocus(`stones.${prev}.stnPopupName`);
                            scrollRowIntoView(prev);
                          }, 150);
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-red-50"
                      >
                        <FaTrash size={10} className="text-gray-500" />
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          const newIndex = fields.length;
                          append(defaultStoneFields);
                          requestAnimationFrame(() => {
                            setTimeout(() => {
                              setFocus(`stones.${newIndex}.stnPopupName`);
                              scrollRowIntoView(newIndex);
                            }, 300);
                          });
                        }}
                        className="border px-2 py-1 rounded text-xs border-gray-400 hover:bg-green-50"
                      >
                        <FaPlus size={10} className="text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Totals chip */}
          <div className="sticky bottom-8 flex justify-center mt-1">
            <div className="bg-violet-100 border border-violet-300 rounded-full text-[11px] px-4 py-1 shadow-md text-violet-700 flex gap-4 transition-all duration-150">
              <span>💎 Total Wt: <strong>{totals.totalWt}</strong></span>
              <span>🔹 Total Pcs: <strong>{totals.totalPcs}</strong></span>
              <span>💰 Total Amt: <strong>{totals.totalAmt}</strong></span>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-[#f7f4ff] z-20 border-t border-violet-200 p-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpenStoneModal(false)}
              className="text-xs px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(handleSaveClick)}
              className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
            >
              Save
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StonePopUp;
